import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { useThemeStore } from '@/store/theme';
import { delta, derive, type Bucket, type DailyPoint } from '@/api/tracker-dashboard';
import { fmtDelta, fmtInt, fmtMoney, fmtPct, paint } from './format';

/**
 * A KPI here is three things at once: the number, how it compares to the same
 * length of time before it, and its shape over the window. The shape is what
 * turns "deposits are down 12%" into "deposits died on Tuesday", so every tile
 * carries a sparkline rather than only the headline.
 */
interface TileProps {
  label: string;
  value: string;
  hint?: string;
  change: number | null;
  /** Whether a rise in this metric is good. Cost-like metrics would flip it. */
  goodUp?: boolean;
  series: number[];
  color: string;
  active?: boolean;
  onClick?: () => void;
}

function Tile({ label, value, hint, change, goodUp = true, series, color, active, onClick }: TileProps) {
  const data = series.map((v, i) => ({ i, v }));
  const rising = change !== null && change > 0;
  const flat = change !== null && Math.abs(change) < 0.05;
  const good = change === null ? true : rising === goodUp;
  const tone = flat
    ? 'bg-surface-muted text-content-subtle'
    : good
      ? 'bg-success-soft text-success'
      : 'bg-danger-soft text-danger';

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`group text-left w-full bg-surface rounded-xl border shadow-sm p-4 transition-all ${
        active
          ? 'border-primary ring-1 ring-primary/30'
          : 'border-border hover:border-border-strong hover:shadow-md'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-content-subtle truncate">{label}</span>
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md shrink-0 ${tone}`}>
          {fmtDelta(change)}
        </span>
      </div>
      <div className="mt-1.5 text-xl font-semibold font-mono tracking-tight text-content">{value}</div>
      {hint && <div className="text-[11px] text-content-subtle mt-0.5 truncate">{hint}</div>}
      <div className="h-8 -mx-1 mt-2">
        {series.some((v) => v > 0) && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`spark-${label.replace(/\W/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone" dataKey="v" stroke={color} strokeWidth={2}
                fill={`url(#spark-${label.replace(/\W/g, '')})`} isAnimationActive={false} dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Wrapper>
  );
}

interface Props {
  totals: Bucket;
  prev: Bucket;
  daily: DailyPoint[];
  metric: string;
  onMetric: (m: string) => void;
}

export function TrackerKpis({ totals, prev, daily, metric, onMetric }: Props) {
  const theme = useThemeStore((s) => s.theme);
  const cur = derive(totals);
  const before = derive(prev);
  const c = (k: string) => paint(k, theme);

  const tiles: (TileProps & { key: string })[] = [
    {
      key: 'clicks',
      label: 'Clicks',
      value: fmtInt(totals.clicks),
      hint: `${fmtPct(cur.uniq_rate, 0)} unique`,
      change: delta(totals.clicks, prev.clicks),
      series: daily.map((d) => d.clicks),
      color: c('clicks'),
    },
    {
      key: 'uniq_clicks',
      label: 'Unique',
      value: fmtInt(totals.uniq_clicks),
      hint: 'first click in window',
      change: delta(totals.uniq_clicks, prev.uniq_clicks),
      series: daily.map((d) => d.uniq_clicks),
      color: c('uniq_clicks'),
    },
    {
      key: 'regs',
      label: 'Registrations',
      value: fmtInt(totals.regs),
      hint: `CR ${fmtPct(cur.cr_reg)}`,
      change: delta(totals.regs, prev.regs),
      series: daily.map((d) => d.regs),
      color: c('regs'),
    },
    {
      key: 'deps',
      label: 'Deposits',
      value: fmtInt(totals.deps),
      hint: `reg→dep ${fmtPct(cur.cr_reg_dep, 1)}`,
      change: delta(totals.deps, prev.deps),
      series: daily.map((d) => d.deps),
      color: c('deps'),
    },
    {
      key: 'revenue',
      label: 'Revenue',
      value: fmtMoney(cur.revenue),
      hint: cur.per_dep > 0 ? `${fmtMoney(cur.per_dep)} / dep` : undefined,
      change: delta(cur.revenue, before.revenue),
      series: daily.map((d) => d.payout_cents / 100),
      color: c('revenue'),
    },
    {
      key: 'cr_dep',
      label: 'CR click→dep',
      value: fmtPct(cur.cr_dep),
      hint: `${fmtInt(totals.deps)} of ${fmtInt(totals.clicks)}`,
      change: delta(cur.cr_dep, before.cr_dep),
      series: daily.map((d) => derive(d).cr_dep),
      color: c('cr_dep'),
    },
    {
      key: 'epc',
      label: 'EPC',
      value: `$${cur.epc.toFixed(3)}`,
      hint: 'revenue per click',
      change: delta(cur.epc, before.epc),
      series: daily.map((d) => derive(d).epc),
      color: c('epc'),
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
      {tiles.map(({ key, ...t }) => (
        <Tile key={key} {...t} active={metric === key} onClick={() => onMetric(key)} />
      ))}
    </div>
  );
}
